/**
 * Auth Store (Zustand + XState)
 *
 * Thin wrapper that exposes the XState auth machine to React via Zustand.
 * Preserves the existing useAuthStore API so consumers don't need changes.
 *
 * Responsibilities:
 * - Derive boolean flags (isAuthenticated, isLoading, isSubmitting) from machine state
 * - Trigger app-level side effects on state transitions (socket, RevenueCat, backend sync)
 * - Provide imperative methods (login, register, logout, etc.)
 * - Wire up the auth provider listener → machine events
 */

import { create } from 'zustand';
import { createActor, type AnyActorRef } from 'xstate';
import { createAuthMachine, type AuthMachineContext, type AuthMachineEvent } from './machine';
import type { AuthProvider, AuthUser, AuthSession, AuthMachineState } from './types';
import { api } from '../services/api';
import { connectSocket, disconnectSocket } from '../services/socket';
import { syncQueue } from '../services/syncQueue';
import { networkMonitor } from '../services/networkMonitor';
import { clearCachedPreferences } from '../services/preferencesCache';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { createLogger } from '../utils/logger';

const log = createLogger('AuthStore');

// ─── Store interface (backwards-compatible) ──────────────────────

interface AuthStoreState {
  user: AuthUser | null;
  session: AuthSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSubmitting: boolean;
  /** Registration succeeded but the email must be confirmed before sign-in */
  emailConfirmationRequired: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  verifyRecoveryCode: (email: string, code: string) => Promise<void>;
  verifySignupCode: (email: string, code: string) => Promise<void>;
  resendSignupConfirmation: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  updateProfile: (data: { name?: string }) => Promise<void>;
  clearError: () => void;
}

// ─── Module state ────────────────────────────────────────────────

let authActor: AnyActorRef | null = null;
let authProvider: AuthProvider | null = null;
let providerListenerUnsub: (() => void) | null = null;

// ─── State derivation ────────────────────────────────────────────

function deriveState(machineValue: string, context: AuthMachineContext) {
  const state = machineValue as AuthMachineState;
  return {
    user: context.user,
    session: context.session,
    error: context.error,
    isAuthenticated: state === 'authenticated',
    isLoading: state === 'restoring',
    isSubmitting: state === 'authenticating' || state === 'loggingOut',
    emailConfirmationRequired: context.emailConfirmationRequired,
  };
}

// ─── Machine promise helper ──────────────────────────────────────

/** Default timeout for login/register (can't safely abort — provider may still succeed) */
const AUTH_TIMEOUT_MS = 30_000;
/** Logout timeout — short because force-reset is safe (no race condition risk) */
const LOGOUT_TIMEOUT_MS = 5_000;

/** States that can accept LOGIN / REGISTER / LOGOUT events */
const READY_STATES: Set<AuthMachineState> = new Set(['idle', 'authenticated']);

/**
 * Send an event to the machine and wait for a terminal state.
 * Handles cleanup (unsubscribe, timeout) so callers can't leak promises.
 *
 * Rejects immediately if the machine is mid-transition (authenticating,
 * loggingOut, restoring) — XState would silently drop the event, and sendAndWait
 * would hang until timeout.
 */
function sendAndWait(
  event: AuthMachineEvent,
  checker: (
    state: AuthMachineState,
    context: AuthMachineContext,
    resolve: () => void,
    reject: (err: Error) => void,
  ) => void,
  timeoutMs = AUTH_TIMEOUT_MS,
): Promise<void> {
  if (!authActor) return Promise.reject(new Error('Auth not initialized'));

  const currentState = authActor.getSnapshot().value as AuthMachineState;
  if (!READY_STATES.has(currentState)) {
    const msg =
      `Auth store: cannot send ${event.type} while in "${currentState}". ` +
      'Callers must check isSubmitting/isLoading before calling auth methods.';
    if (__DEV__) {
      // Fail loud in development so the misuse is caught immediately
      throw new Error(msg);
    }
    log.error(msg);
    return Promise.reject(new Error(msg));
  }

  return new Promise<void>((resolve, reject) => {
    let settled = false;

    const cleanup = () => {
      settled = true;
      clearTimeout(timer);
      sub.unsubscribe();
    };

    const safeResolve = () => { if (!settled) { cleanup(); resolve(); } };
    const safeReject = (err: Error) => { if (!settled) { cleanup(); reject(err); } };

    const timer = setTimeout(() => {
      safeReject(new Error('Auth operation timed out'));
    }, timeoutMs);

    const sub = authActor!.subscribe({
      next: (snapshot) => {
        if (settled) return;
        checker(
          snapshot.value as AuthMachineState,
          snapshot.context,
          safeResolve,
          safeReject,
        );
      },
      complete: () => {
        // Actor was stopped (e.g. destroyAuth called mid-flow)
        safeReject(new Error('Auth actor stopped'));
      },
    });

    authActor!.send(event);
  });
}

// ─── Store ───────────────────────────────────────────────────────

export const useAuthStore = create<AuthStoreState>((_set, _get) => ({
  user: null,
  session: null,
  isAuthenticated: false,
  isLoading: true,
  isSubmitting: false,
  emailConfirmationRequired: false,
  error: null,

  login: (email, password) =>
    sendAndWait({ type: 'LOGIN', email, password }, (state, ctx, resolve, reject) => {
      if (state === 'authenticated') {
        resolve();
      } else if (state === 'idle') {
        reject(new Error(ctx.error || 'Login failed'));
      }
    }),

  register: (email, password, name) =>
    sendAndWait({ type: 'REGISTER', email, password, name }, (state, ctx, resolve, reject) => {
      if (state === 'authenticated') {
        resolve();
      } else if (state === 'idle') {
        if (ctx.emailConfirmationRequired) {
          resolve(); // Registration succeeded, email verification needed
        } else {
          reject(new Error(ctx.error || 'Registration failed'));
        }
      }
    }),

  logout: async () => {
    if (!authActor) return;
    try {
      await sendAndWait({ type: 'LOGOUT' }, (state, _ctx, resolve) => {
        if (state === 'idle') {
          onLoggedOut();
          resolve();
        }
      }, LOGOUT_TIMEOUT_MS);
    } catch (err) {
      // Logout is best-effort — callers rely on isAuthenticated becoming false
      // to trigger navigation (reactive, not imperative). If the machine got stuck
      // (e.g. provider signOut froze, actor stopped), force the store to a clean
      // state so the UI isn't left in a broken isSubmitting=true limbo.
      log.warn('Logout did not complete cleanly, forcing state reset', err);
      onLoggedOut();
      useAuthStore.setState({
        user: null,
        session: null,
        isAuthenticated: false,
        isSubmitting: false,
        error: null,
      });
    }
  },

  // These don't change auth state — call the provider directly.
  // Errors are already normalized by the adapter (AuthProviderError).
  resetPassword: async (email) => {
    if (!authProvider) throw new Error('Auth not initialized');
    await authProvider.resetPassword(email, { redirectTo: 'fluencia://reset-password' });
  },

  // Establishes a provider-level recovery session (Supabase persists it and
  // fires SIGNED_IN, which the listener below ignores while the machine is
  // idle) — exactly how the reset deep link is treated today. The machine is
  // NOT transitioned: isAuthenticated stays false so the user remains in the
  // signed-out navigation group and finishes on ResetPasswordScreen with
  // updatePassword. The session only enters the machine through the existing
  // restore/login paths, so onAuthenticated side effects run exactly once.
  verifyRecoveryCode: async (email, code) => {
    if (!authProvider) throw new Error('Auth not initialized');
    await authProvider.verifyRecoveryCode(email, code);
  },

  // Typed-code sign-up confirmation (AUTH-013). UNLIKE recovery, verify
  // success signs the user in: the provider call runs directly so the typed
  // AuthProviderError reaches the screen (routing through the machine's
  // onError would stringify it and lose the code), then SIGNUP_VERIFIED
  // moves the machine idle → authenticated. Side effects fire exactly once
  // via the existing transition subscription in initializeAuth. If the app
  // dies between verifyOtp success and the event, the Supabase-persisted
  // session restores on next boot — the states converge.
  verifySignupCode: async (email, code) => {
    if (!authProvider) throw new Error('Auth not initialized');
    const result = await authProvider.verifySignupCode(email, code);
    authActor?.send({
      type: 'SIGNUP_VERIFIED',
      user: result.user,
      session: result.session,
    });
  },

  resendSignupConfirmation: async (email) => {
    if (!authProvider) throw new Error('Auth not initialized');
    await authProvider.resendSignupConfirmation(email);
  },

  updatePassword: async (newPassword) => {
    if (!authProvider) throw new Error('Auth not initialized');
    await authProvider.updatePassword(newPassword);
  },

  updateProfile: async (data) => {
    if (!authProvider) throw new Error('Auth not initialized');
    await authProvider.updateUserMetadata(data);
  },

  clearError: () => {
    authActor?.send({ type: 'CLEAR_ERROR' });
  },
}));

// ─── Side effects on state transitions ───────────────────────────

function onAuthenticated(user: AuthUser) {
  // Start auth-dependent services (no API calls — those are driven by AppNavigator)
  networkMonitor.start();
  syncQueue.initialize().catch((err) => {
    log.warn('Failed to initialize sync queue', err);
  });
  useSubscriptionStore.getState().loginWithUserId(user.id).catch((err) => {
    log.warn('Failed to login to RevenueCat', err);
  });
  connectSocket().catch((err) => {
    log.warn('Failed to connect socket', err);
  });
}

function onLoggedOut() {
  disconnectSocket();
  networkMonitor.stop();
  useSubscriptionStore.getState().logout().catch((err) => {
    log.warn('Failed to logout from RevenueCat', err);
  });
  api.resetSyncRecovery();
  syncQueue.destroy();
  clearCachedPreferences().catch(() => {});
}

// ─── Initialization ──────────────────────────────────────────────

/**
 * Initialize the auth module with a provider.
 * Creates the XState machine, starts the actor, and wires up the provider listener.
 * Call once at app startup.
 */
export function initializeAuth(provider: AuthProvider) {
  authProvider = provider;

  // Initialize RevenueCat before restoring session
  useSubscriptionStore.getState().initialize().catch((err) => {
    log.warn('Failed to initialize RevenueCat', err);
  });

  const machine = createAuthMachine({ provider });

  authActor = createActor(machine);

  // Sync machine state → Zustand store + trigger side effects on transitions
  let previousState: AuthMachineState = 'idle';
  authActor.subscribe((snapshot) => {
    const currentState = snapshot.value as AuthMachineState;
    const derived = deriveState(currentState, snapshot.context);
    useAuthStore.setState(derived);

    // Fire side effects on entry to authenticated (login, register, or session restore)
    if (currentState === 'authenticated' && previousState !== 'authenticated') {
      onAuthenticated(snapshot.context.user!);
    }

    previousState = currentState;
  });

  // Provider events → machine (only when authenticated, for token refresh / sign-out)
  providerListenerUnsub = provider.onAuthStateChange((event, user, session) => {
    log.debug('Auth provider event', { event });

    if (!authActor) return;
    const currentState = authActor.getSnapshot().value as AuthMachineState;

    // During active flows (authenticating, restoring), ignore provider events
    // to prevent race conditions — the machine is already handling the transition
    if (currentState === 'authenticating' || currentState === 'restoring') {
      log.debug('Ignoring provider event during active flow', { event, currentState });
      return;
    }

    if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
      authActor.send({ type: 'PROVIDER_EVENT', event, user, session });
    }

    // External sign-out (e.g. from another device/tab)
    if (event === 'SIGNED_OUT' && currentState === 'authenticated') {
      authActor.send({ type: 'LOGOUT' });
    }
  });

  authActor.start();

  // Kick off session restoration
  authActor.send({ type: 'RESTORE' });
}

/**
 * Get the auth provider instance (for API/socket token access).
 * Throws if called before initializeAuth.
 */
export function getAuthProvider(): AuthProvider {
  if (!authProvider) throw new Error('Auth not initialized — call initializeAuth first');
  return authProvider;
}

/**
 * Tear down the auth module (for testing).
 */
export function destroyAuth() {
  providerListenerUnsub?.();
  providerListenerUnsub = null;
  authActor?.stop();
  authActor = null;
  authProvider = null;
}
